import { AlertTriangle, Boxes, PackageX, Wallet } from "lucide-react";

import { Card } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import type { InventoryOverview, StockMovement } from "@/repositories/catalogueRepository";

/**
 * The panels around the Inventory table: four KPI cards, a status doughnut, the
 * reorder list and the movement feed.
 *
 * Every figure here comes from the ledger. None of it is illustrative — an
 * unstocked shop shows zeroes and empty panels, which is the truth, rather than
 * the design's sample numbers.
 */

// ═════════════════════════════════════════════════════════════════════════════
// KPI cards
// ═════════════════════════════════════════════════════════════════════════════
const TONES = {
  brand: { tile: "bg-brand-50 text-brand-600", value: "text-ink-900" },
  info: { tile: "bg-info-50 text-info-500", value: "text-ink-900" },
  warn: { tile: "bg-warn-50 text-warn-700", value: "text-warn-700" },
  danger: { tile: "bg-danger-50 text-danger-700", value: "text-danger-700" },
} as const;

function Kpi({
  icon: Icon,
  label,
  value,
  caption,
  tone,
}: {
  icon: typeof Boxes;
  label: string;
  value: string;
  caption: string;
  tone: keyof typeof TONES;
}) {
  return (
    <Card className="flex items-center gap-4">
      <span
        className={cn("grid size-12 shrink-0 place-items-center rounded-full", TONES[tone].tile)}
        aria-hidden
      >
        <Icon className="size-6" strokeWidth={1.75} />
      </span>
      <div className="min-w-0">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-500">
          {label}
        </p>
        <p className={cn("text-kpi tabular-nums", TONES[tone].value)}>{value}</p>
        <p className="truncate text-xs text-ink-500">{caption}</p>
      </div>
    </Card>
  );
}

export function KpiRow({ summary }: { summary: InventoryOverview["summary"] }) {
  const canSeeValue = summary.total_stock_value !== undefined;

  return (
    <div
      className={cn(
        "grid gap-4",
        // Three columns when stock value is hidden, so a cashier does not get a
        // gap where a card they may not see would have been.
        canSeeValue ? "sm:grid-cols-2 xl:grid-cols-4" : "sm:grid-cols-3",
      )}
    >
      <Kpi
        icon={Boxes}
        tone="brand"
        label="Total products"
        value={String(summary.total_products)}
        caption="All active products"
      />
      {canSeeValue && (
        <Kpi
          icon={Wallet}
          tone="info"
          label="Total stock value"
          value={formatMoney(summary.total_stock_value!)}
          caption="At cost, current stock"
        />
      )}
      <Kpi
        icon={AlertTriangle}
        tone="warn"
        label="Low stock items"
        value={String(summary.low_stock_items)}
        caption={summary.low_stock_items === 0 ? "Nothing to reorder" : "Reorder recommended"}
      />
      <Kpi
        icon={PackageX}
        tone="danger"
        label="Out of stock items"
        value={String(summary.out_of_stock_items)}
        caption={
          summary.out_of_stock_items === 0 ? "Everything in stock" : "Requires immediate attention"
        }
      />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Stock status doughnut
// ═════════════════════════════════════════════════════════════════════════════
const SLICES = [
  { key: "IN_STOCK", label: "In stock", colour: "#0EA35A" },
  { key: "LOW_STOCK", label: "Low stock", colour: "#F59E0B" },
  { key: "OUT_OF_STOCK", label: "Out of stock", colour: "#EF4444" },
] as const;

/**
 * Drawn with SVG arcs rather than a charting library.
 *
 * One doughnut of three slices does not justify 40 KB of JavaScript on a device
 * that may be loading over 3G — and a chart library would be the single largest
 * dependency in the bundle for this one component.
 */
export function StatusDoughnut({ breakdown }: { breakdown: Record<string, number> }) {
  const total = breakdown.total ?? 0;
  const radius = 56;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;

  return (
    <Card>
      <h2 className="text-base font-semibold text-ink-900">Stock status overview</h2>

      {total === 0 ? (
        <p className="py-10 text-center text-sm text-ink-500">
          Nothing to show until stock is received.
        </p>
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-6">
          <svg viewBox="0 0 140 140" className="size-36 shrink-0 -rotate-90" role="img">
            <title>Stock status</title>
            <circle cx="70" cy="70" r={radius} fill="none" stroke="#E9EDF2" strokeWidth="22" />
            {SLICES.map((slice) => {
              const count = breakdown[slice.key] ?? 0;
              const length = (count / total) * circumference;
              const element = (
                <circle
                  key={slice.key}
                  cx="70"
                  cy="70"
                  r={radius}
                  fill="none"
                  stroke={slice.colour}
                  strokeWidth="22"
                  strokeDasharray={`${length} ${circumference - length}`}
                  strokeDashoffset={-offset}
                />
              );
              offset += length;
              return element;
            })}
          </svg>

          <dl className="min-w-0 flex-1 space-y-2.5">
            {SLICES.map((slice) => {
              const count = breakdown[slice.key] ?? 0;
              return (
                <div key={slice.key} className="flex items-center gap-2 text-sm">
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: slice.colour }}
                    aria-hidden
                  />
                  <dt className="flex-1 text-ink-600">{slice.label}</dt>
                  <dd className="tabular-nums font-medium text-ink-900">
                    {count}{" "}
                    <span className="text-ink-500">
                      ({total === 0 ? 0 : Math.round((count / total) * 100)}%)
                    </span>
                  </dd>
                </div>
              );
            })}
            <div className="flex items-center gap-2 border-t border-line pt-2.5 text-sm">
              <dt className="flex-1 font-medium text-ink-900">Total</dt>
              <dd className="tabular-nums font-semibold text-ink-900">{total}</dd>
            </div>
          </dl>
        </div>
      )}
    </Card>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Reorder list
// ═════════════════════════════════════════════════════════════════════════════
export function LowStockPanel({
  items,
  onView,
}: {
  items: InventoryOverview["low_stock"];
  onView: () => void;
}) {
  return (
    <Card>
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-base font-semibold text-ink-900">Top low stock items</h2>
        {items.length > 0 && (
          <button
            type="button"
            onClick={onView}
            className="text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            View all
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <p className="py-10 text-center text-sm text-ink-500">
          Nothing is below its reorder level.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-line">
          {items.map((item) => (
            <li key={item.product_id} className="flex items-center gap-3 py-2.5">
              <ProductTile name={item.name} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink-900">{item.name}</p>
                <p className="text-xs text-warn-700">
                  {Number(item.quantity)} {item.unit} left · reorder at {Number(item.reorder_level)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Movement feed
// ═════════════════════════════════════════════════════════════════════════════
const MOVEMENT_LABEL: Record<string, string> = {
  OPENING: "Opening balance",
  RECEIPT: "Received",
  SALE: "Sale",
  REFUND: "Customer return",
  ADJUSTMENT: "Adjusted",
  COUNT: "Stock count",
  TRANSFER_IN: "Transferred in",
  TRANSFER_OUT: "Transferred out",
  WASTAGE: "Wastage",
  RETURN_TO_SUPPLIER: "Returned to supplier",
};

export function MovementsPanel({
  movements,
  onView,
}: {
  movements: StockMovement[];
  onView: () => void;
}) {
  return (
    <Card>
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-base font-semibold text-ink-900">Recent stock movements</h2>
        {movements.length > 0 && (
          <button
            type="button"
            onClick={onView}
            className="text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            View all
          </button>
        )}
      </div>

      {movements.length === 0 ? (
        <p className="py-10 text-center text-sm text-ink-500">No stock has moved yet.</p>
      ) : (
        <ul className="mt-3 divide-y divide-line">
          {movements.map((movement) => {
            const quantity = Number(movement.quantity);
            return (
              <li key={movement.id} className="flex items-center gap-3 py-2.5">
                <ProductTile name={movement.product_name} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink-900">
                    {movement.product_name}
                  </p>
                  <p className="truncate text-xs text-ink-500">
                    {MOVEMENT_LABEL[movement.movement_type] ?? movement.movement_type}
                    {movement.reason && ` · ${movement.reason}`}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p
                    className={cn(
                      "text-sm font-semibold tabular-nums",
                      quantity >= 0 ? "text-brand-600" : "text-danger-700",
                    )}
                  >
                    {quantity > 0 ? "+" : ""}
                    {quantity} units
                  </p>
                  <p className="text-xs text-ink-500">
                    {new Date(movement.occurred_at).toLocaleTimeString("en-ZA", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

/**
 * A product's picture, or something better than an empty square.
 *
 * Most products have a photograph — their own, or the shared catalogue's. The
 * ones that do not fall back to initials on a colour derived from the name:
 * stable across renders, and enough to make a list scannable. That fallback is
 * not temporary scaffolding any more. A shop photographs the things it sells a
 * lot and never gets round to the rest, and a wall of grey squares would be a
 * worse list than a wall of initials.
 */
export function ProductTile({
  name,
  src,
  className,
}: {
  name: string;
  src?: string | null;
  className?: string;
}) {
  const initials = name
    .split(/\s+/)
    .filter((word) => /[a-z]/i.test(word[0] ?? ""))
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join("");

  let hash = 0;
  for (const character of name) hash = (hash * 31 + character.charCodeAt(0)) % 360;

  if (src) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        className={cn(
          "size-9 shrink-0 rounded-control border border-line bg-surface object-contain p-0.5",
          className,
        )}
      />
    );
  }

  return (
    <span
      className={cn(
        "grid size-9 shrink-0 place-items-center rounded-control text-xs font-semibold",
        className,
      )}
      style={{
        backgroundColor: `hsl(${hash} 70% 94%)`,
        color: `hsl(${hash} 55% 32%)`,
      }}
      aria-hidden
    >
      {initials || "?"}
    </span>
  );
}
